import { Button } from "@/components/ui/button";
import {
  Banknote,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  Loader2,
  ShoppingCart,
  X,
} from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useEffect, useState } from "react";
import { useOrders } from "@/hooks/useOrders";
import type { OrderType } from "@/types/order";
import {
  format,
  isSameDay,
  isSameMonth,
  isSameWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { useTranslation } from "react-i18next";

export default function ordersScreen() {
  const [isOpen, setIsOpen] = useState(false);

  const { t, i18n } = useTranslation();

  const generateOrderRecord = (orders: OrderType[]) => {
    const record: Record<string, OrderType[]> = {};
    record.today = [];
    record.yesterday = [];
    record.last_week = [];
    record.last_month = [];

    orders.forEach((order) => {
      // today
      if (isSameDay(order.created_at, new Date())) {
        record.today.push(order);
      }

      // yesterday
      else if (isSameDay(order.created_at, subDays(new Date(), 1))) {
        record.yesterday.push(order);
      }

      // last week
      else if (isSameWeek(order.created_at, new Date())) {
        record.last_week.push(order);
      }

      // last month
      else if (isSameMonth(order.created_at, new Date())) {
        record.last_month.push(order);
      }

      // other
      else {
        const key = format(order.created_at, "MMM yyyy");
        record[`${key}`] = record[`${key}`] || [];
        record[`${key}`].push(order);
      }
    });

    return record;
  };

  const { data: orders, isLoading, isError } = useOrders();

  const [orderRecord, setOrderRecord] = useState<Record<string, OrderType[]>>(
    {}
  );

  useEffect(() => {
    if (orders) {
      console.log("orders", orders);
      const record = generateOrderRecord(orders);
      setOrderRecord(record);
    }
  }, [orders]);

  const renderOrder = (order: OrderType) => {
    return (
      <Collapsible className="flex flex-col gap-1">
        <CollapsibleTrigger className="relative w-full border border-gray-200 hover:ring-2 hover:ring-primary cursor-pointer rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex gap-8">
            <div
              className={`flex h-12 w-12 items-center justify-center gap-2 rounded-full p-2 ${
                order.status === "received" ? "bg-green-100" : "bg-yellow-100"
              }`}
            >
              {order.status === "received" ? (
                <Check className="text-green-500" />
              ) : (
                <Clock className="text-yellow-500" />
              )}
            </div>
            <div className="flex flex-col gap-1 items-start">
              <p className="font-semibold">
                {t("order")} #{order.order_number} · Burger King
              </p>
              <p className="text-gray-500 text-xs">
                {format(order.created_at, "dd/MM/yyyy")}
              </p>
            </div>
            <div className="flex items-center gap-4 ms-auto">
              <div
                className={`px-2 py-1 bg-blue-100 border flex justify-center items-center gap-2 rounded-full  ${
                  order.payment_method === "visa"
                    ? "bg-blue-100 border-blue-500 text-blue-500"
                    : "bg-green-100 border-green-500 text-green-500"
                }`}
              >
                {order.payment_method === "visa" ? (
                  <CreditCard className="w-5 h-5" />
                ) : (
                  <Banknote className="w-5 h-5" />
                )}
                <span className="text-xs">
                  {order.payment_method === "visa"
                    ? t("credit_card")
                    : t("cash")}
                </span>
              </div>
              <p className="font-semibold">{order.total_price}₪</p>
              <ChevronDown />
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="flex flex-col items-end gap-4 p-4 px-16  border border-gray-200 rounded-lg leading-loose shadow-sm">
            <p className="w-full">
              {order.order_items.map((order_item, index) => (
                <span key={order_item.item_id}>
                  <span className="font-semibold">
                    {i18n.language === "ar"
                      ? order_item.item_name_ar
                      : order_item.item_name_en}
                  </span>{" "}
                  (₪{order_item.item_price})
                  {index !== order.order_items.length - 1 && " · "}
                </span>
              ))}
            </p>
            {/* <p>
              {order.order_items.map((order_item) => (
                <span className="font-semibold">{order_item.item_name_ar}</span> (₪{order_item.item_price})
              ))} */}
            {/* <span className="font-semibold">Huge Tasty Burger</span> (30.00₪)
              · <span className="font-semibold">Huge Tasty Burger</span>{" "}
              (30.00₪) ·{" "}
              <span className="font-semibold">Huge Tasty Burger</span> (30.00₪)
              · <span className="font-semibold">Huge Tasty Burger</span>{" "}
              (30.00₪) */}
            {/* </p> */}
            <Button className="">{t("order_again")}</Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="p-4 h-full w-full flex justify-center">
      <div className="h-full w-full md:w-3/4 lg:w-1/2 py-4 md:py-8 lg:py-16 flex flex-col gap-12">
        <h1 className="text-2xl font-bold">{t("my_orders")}</h1>
        {isLoading && <Loader2 className="w-10 h-10 animate-spin" />}
        <div className="flex flex-col gap-8">
          {Object.keys(orderRecord).map((key) => {
            if (orderRecord[key].length === 0) {
              return null;
            }
            return (
              <div className="flex flex-col gap-4">
                <p className="font-bold text-2xl">{t(key)}</p>
                {orderRecord[key].map((order) => renderOrder(order))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
